import { createContext, FC, ReactElement, useCallback, useContext, useState } from 'react'
import { cardTokenizer } from '../../externals/api/acquirer'
import { get as getPlans, Plan, PlanOptions } from '../../externals/api/plans'
import { cancel as cancelApi, Statement, statement as statementApi, subscribe as subscribeApi } from '../../externals/api/subscriptions'
import { useAlertHandler } from '../alertHandler'

type Subscription = {
  number: string
  holderName: string
  expMonth: string
  expYear: string
  cvv: string
  couponCode?: string
  zipCode: string
  personNumber: string
  planId: string
}
interface IPlans {
  plans: Plan[]
  statements: Statement[]
  get: (params: PlanOptions) => Promise<Plan[]>
  getStatement: () => Promise<Statement[]>
  cancel: (reason?: string) => Promise<void>
  subscribe: (params: Subscription) => Promise<void>
}

const PlansContext = createContext<IPlans>({} as IPlans)

export const usePlans = (): IPlans => {
  const context = useContext(PlansContext)
  if (!context) {
    throw new Error('usePlans must be used within an PlansContext')
  }

  return context
}

export const PlansProvider: FC = ({ children }): ReactElement => {
  const [plans, setPlans] = useState<Plan[]>([])
  const [statements, setStatement] = useState<Statement[]>([])
  const { showError } = useAlertHandler()

  const get = useCallback(async (params: PlanOptions): Promise<Plan[]> => {
    try {
      const response = await getPlans(params)
      setPlans(response)

      return response
    } catch (err) {
      showError(err)

      throw err
    }
  }, [setPlans, showError])

  const getStatement = useCallback(async (): Promise<Statement[]> => {
    try {
      const response = await statementApi()
      setStatement(response)

      return response
    } catch (err) {
      showError(err)

      throw err
    }
  }, [setPlans, showError])

  const cancel = useCallback(async (reason?: string): Promise<void> => {
    try {
      await cancelApi({ reason })
    } catch (err) {
      showError(err)

      throw err
    }
  }, [setPlans, showError])

  const subscribe = useCallback(async (params: Subscription): Promise<void> => {
    try {
      const cardToken = await cardTokenizer({
        number: params.number,
        holderName: params.holderName,
        expMonth: params.expMonth,
        expYear: params.expYear,
        cvv: params.cvv
      })

      await subscribeApi({
        cardToken,
        couponCode: params.couponCode,
        zipCode: params.zipCode,
        personNumber: params.personNumber,
        planId: params.planId,
        ownerName: params.holderName
      })
    } catch (err) {
      showError(err)

      throw err
    }
  }, [setPlans, showError])

  return (
    <PlansContext.Provider
      value={{
        plans,
        statements,
        cancel,
        get,
        subscribe,
        getStatement
      }}
    >
      {children}
    </PlansContext.Provider>
  )
}
